import { useGateValue } from "@statsig/react-bindings";
import { useMemo } from "react";
import { BILLING_GATE } from "@/types/billing.ts";
import { MY_COMPUTER_GATE } from "@/types/userMachines.ts";
import { useTaskGates } from "@/components/userTasks/useTaskGates.ts";
import { useRoutinesV2 } from "@/components/routines/useRoutinesV2.ts";
import { ROUTINES_V2_CONFIG } from "@/types/routinesV2.ts";
import {
  Bell,
  BookOpenText,
  Cable,
  CalendarPlus,
  Computer,
  CreditCard,
  Gift,
  Laptop,
  Lightbulb,
  type LucideIcon,
  MapPin,
  Package,
  Sparkles,
  UserRound,
  UsersRound,
  Wrench,
  Zap,
  Search,
} from "lucide-react";

/**
 * Settings navigation data, shared between the Settings view and the mobile
 * nav sheet (AppNavMenuButton.tsx) so the tab list, the promoted destinations,
 * and the mobile drill-in rows cannot drift apart.
 */

export const CONTACTS_GATE = "contacts_ui";

/** Skills is internal-only for now, in the nav list as well as here. */
export const SKILLS_GATE = "internal-features";

/** Nav order of the settings sections. Gated tabs are filtered out in place. */
export const SETTINGS_TABS = [
  // The default tab, so it lives at bare `/settings`; `/settings/account`
  // still resolves for links that predate the move. The label is plural
  // because the tab manages every connected account, not just the active one.
  { value: "account", label: "Accounts" },
  // Routines, Connectors and Skills in the order a user meets them: what okthx
  // runs on its own, what it is plugged into, what it knows how to do. Routines
  // leads because it is the one that acts without being asked. The order holds
  // here whichever of them the gear ends up listing.
  { value: "routines", label: "Routines" },
  // Behind the routines_v2 dynamic config, keyed by its name in the gate map
  // below like a gate.
  { value: "packs", label: "Packs", gate: ROUTINES_V2_CONFIG },
  { value: "connectors", label: "Connectors" },
  { value: "skills", label: "Skills", gate: SKILLS_GATE },
  { value: "contacts", label: "People", gate: CONTACTS_GATE },
  { value: "library", label: "Search" },
  { value: "scheduling", label: "Scheduling" },
  // "location" keeps its value and /settings/location route; only the visible
  // label is plural, since the page holds home, work, and device locations.
  { value: "location", label: "Locations" },
  { value: "memories", label: "Memories" },
  { value: "notifications", label: "Notifications" },
  { value: "mcp", label: "MCP" },
  { value: "computers", label: "Computers", gate: MY_COMPUTER_GATE },
  { value: "assistant", label: "Assistant" },
  { value: "billing", label: "Billing", gate: BILLING_GATE },
  { value: "referrals", label: "Referrals" },
  // "app" keeps its value and /settings/app route; only the visible label
  // changed to General.
  { value: "app", label: "General" },
] as const satisfies readonly { value: string; label: string; gate?: string }[];

export type SettingsTabValue = (typeof SETTINGS_TABS)[number]["value"];

/**
 * One icon per tab, shared by the desktop rail, the nav sheet, and the mobile
 * drill-in list so the same destination never wears two different icons.
 * `Record<SettingsTabValue, ...>` makes adding a tab without an icon a type
 * error.
 */
export const SETTINGS_TAB_ICONS: Record<SettingsTabValue, LucideIcon> = {
  connectors: Cable,
  // The bolt is routines: a routine runs as a thread, and the thread filter
  // for them wears it too. Skills are the book.
  routines: Zap,
  skills: BookOpenText,
  packs: Package,
  contacts: UsersRound,
  library: Search,
  scheduling: CalendarPlus,
  location: MapPin,
  memories: Lightbulb,
  notifications: Bell,
  // Computer rather than native's Cable: on web, Cable already means
  // Connectors, and the two can appear in the same list.
  mcp: Computer,
  // Laptop rather than Computer, which MCP already wears above, and the two
  // can appear in the same list.
  computers: Laptop,
  assistant: Sparkles,
  account: UserRound,
  billing: CreditCard,
  referrals: Gift,
  app: Wrench,
};

/**
 * The settings tabs this account can actually see, with the gated ones dropped.
 *
 * A hook rather than a constant because several of the tabs are behind Statsig
 * gates, and it lives here beside the list it filters so the settings screen and
 * the sidebar's settings menu can't end up offering different tabs.
 *
 * Every gate is called unconditionally, so adding a gated tab means adding a
 * line here as well as to {@link SETTINGS_TABS}. That is the one registry in
 * this file a missing entry does not turn into a type error — a tab whose gate
 * is absent from the map is simply never shown.
 */
export const useEnabledSettingsTabs = () => {
  const contactsEnabled = useGateValue(CONTACTS_GATE);
  const billingEnabled = useGateValue(BILLING_GATE);
  const skillsEnabled = useGateValue(SKILLS_GATE);
  const myComputerEnabled = useGateValue(MY_COMPUTER_GATE);
  const routinesV2 = useRoutinesV2();
  const gateEnabled: Record<string, boolean> = {
    [ROUTINES_V2_CONFIG]: routinesV2.enabled,
    [CONTACTS_GATE]: contactsEnabled,
    [BILLING_GATE]: billingEnabled,
    [SKILLS_GATE]: skillsEnabled,
    [MY_COMPUTER_GATE]: myComputerEnabled,
  };

  return SETTINGS_TABS.filter((tab) => !("gate" in tab) || gateEnabled[tab.gate]);
};

/**
 * Tabs the settings menu does not offer, because the app reaches them somewhere
 * better: they are canvas views named in the sidebar's nav list. Listing them
 * under the gear as well would put a second door on the same room, and the two
 * doors open different rooms — the settings copy of a page is not the canvas
 * one.
 *
 * Membership follows the nav list, so these two are the ones it always carries.
 * Routines and Skills join them for accounts outside the tasks rollout — see
 * {@link useDescriptiveTabsInSettings}.
 *
 * They keep their tabs and routes either way: `/settings/connectors` is still
 * what a saved link resolves to, and the settings screen still renders them.
 */
const CANVAS_OWNED_TABS: ReadonlySet<string> = new Set(["connectors", "library"]);

/**
 * The two that describe what okthx can do rather than being somewhere the user
 * goes to work. Where they live is one decision, read in several places, so no
 * two surfaces can answer it differently.
 *
 * Connectors is not among them: what the assistant is plugged into governs what
 * it can do at all, which the user reaches for far more often than the routines
 * and skills built on top of it.
 */
export const DESCRIPTIVE_TABS = ["routines", "skills"] as const;

/**
 * Whether the two are settings rows. Tasks takes a top-level slot only inside
 * its rollout, and that is what makes room for them to move under the gear;
 * every other account keeps reaching them from the nav list, where they have
 * always been. Keyed on the same answer that puts Tasks in the nav, so the slot
 * and the thing it displaces cannot disagree.
 */
export const useDescriptiveTabsInSettings = (): boolean => useTaskGates().any;

/**
 * What the gear's menu lists: the enabled tabs, minus the ones the app's own
 * navigation already carries.
 */
export const useSettingsMenuTabs = () => {
  const inSettings = useDescriptiveTabsInSettings();
  return useEnabledSettingsTabs().filter(
    (tab) =>
      !CANVAS_OWNED_TABS.has(tab.value) &&
      (inSettings || !DESCRIPTIVE_TABS.some((value) => value === tab.value)),
  );
};

/** "account" is the default tab, so it lives at bare `/settings`. */
export const settingsTabPath = (tab: string) =>
  tab === "account" ? "/settings" : `/settings/${tab}`;

export const settingsTabLabel = (value: string) =>
  SETTINGS_TABS.find((tab) => tab.value === value)?.label ?? "Settings";

/**
 * Destinations the mobile nav sheet promotes out of the settings list. Paths
 * are explicit rather than via settingsTabPath: on mobile, bare `/settings`
 * (settingsTabPath's answer for the default tab) is the drill-in list, and the
 * sheet must land on the promoted panel itself.
 */
const DESCRIPTIVE_NAV_ITEMS = [
  {
    value: "routines",
    label: "Routines",
    path: "/settings/routines",
    icon: SETTINGS_TAB_ICONS.routines,
  },
  {
    value: "skills",
    label: "Skills",
    path: "/settings/skills",
    icon: SETTINGS_TAB_ICONS.skills,
    gate: SKILLS_GATE,
  },
] as const satisfies readonly {
  value: SettingsTabValue;
  label: string;
  path: string;
  icon: LucideIcon;
  gate?: string;
}[];

// A canvas-owned tab still carries its settings path here, as Search has all
// along: these are the sheet's rows, and the sheet lists settings panels. The
// canvas address is the one the nav itself names.
const ALWAYS_PROMOTED_NAV_ITEMS = [
  {
    value: "connectors",
    label: "Connectors",
    path: "/settings/connectors",
    icon: SETTINGS_TAB_ICONS.connectors,
  },
  {
    value: "library",
    label: "Search",
    path: "/settings/library",
    icon: SETTINGS_TAB_ICONS.library,
  },
] as const satisfies readonly {
  value: SettingsTabValue;
  label: string;
  path: string;
  icon: LucideIcon;
  gate?: string;
}[];

/**
 * What the mobile nav sheet offers. The descriptive destinations are part
 * of it only while they are nav rows rather than settings rows, which is the
 * same condition every other surface reads.
 */
export const usePromotedNavItems = (): readonly {
  value: SettingsTabValue;
  label: string;
  path: string;
  icon: LucideIcon;
  gate?: string;
}[] => {
  const inSettings = useDescriptiveTabsInSettings();
  return useMemo(
    () =>
      inSettings
        ? ALWAYS_PROMOTED_NAV_ITEMS
        : [...DESCRIPTIVE_NAV_ITEMS, ...ALWAYS_PROMOTED_NAV_ITEMS],
    [inSettings],
  );
};

/**
 * Promoted tabs are reached from the nav sheet rather than the mobile settings
 * list, so backing out of them exits to home instead of the list.
 */
export const usePromotedTabs = (): ReadonlySet<string> => {
  const items = usePromotedNavItems();
  return useMemo(() => new Set(items.map((item) => item.value)), [items]);
};

/**
 * The mobile drill-in list shows only the unpromoted tabs, in its own order:
 * Accounts leads, since it is what people come to Settings for, and General
 * closes as the miscellany. Labels resolve through SETTINGS_TABS so the two
 * can't drift; a partition test asserts promoted + rows covers every tab.
 * Gated rows render only while their tab passes the same gate filter.
 */
type MobileSettingsRow = { value: SettingsTabValue; gate?: string };

const MOBILE_ROWS_HEAD = [
  { value: "account" },
  { value: "billing", gate: BILLING_GATE },
] as const satisfies readonly MobileSettingsRow[];

/**
 * Between Billing and People, the order the gear lists them in. Rows here only
 * while the two are settings rows: the sheet that would otherwise carry them is
 * not mounted on any screen, so when they are settings rows the drill-in list
 * is a phone's only way to them.
 */
const DESCRIPTIVE_MOBILE_ROWS = [
  { value: "routines" },
  { value: "skills", gate: SKILLS_GATE },
] as const satisfies readonly MobileSettingsRow[];

const MOBILE_ROWS_TAIL = [
  // Packs is a settings row in both arrangements: no nav sheet promotes it.
  { value: "packs", gate: ROUTINES_V2_CONFIG },
  // People is not a descriptive row, so it stays listed whether or not those
  // are, and leads the tail to sit beside them. Putting it among them would
  // hide it from a phone outside the tasks rollout.
  { value: "contacts", gate: CONTACTS_GATE },
  { value: "scheduling" },
  { value: "location" },
  { value: "memories" },
  { value: "notifications" },
  { value: "mcp" },
  { value: "computers", gate: MY_COMPUTER_GATE },
  { value: "assistant" },
  { value: "referrals" },
  { value: "app" },
] as const satisfies readonly MobileSettingsRow[];

export const useMobileSettingsRows = (): readonly MobileSettingsRow[] => {
  const inSettings = useDescriptiveTabsInSettings();
  return useMemo(
    () =>
      inSettings
        ? [...MOBILE_ROWS_HEAD, ...DESCRIPTIVE_MOBILE_ROWS, ...MOBILE_ROWS_TAIL]
        : [...MOBILE_ROWS_HEAD, ...MOBILE_ROWS_TAIL],
    [inSettings],
  );
};

/**
 * The list's own URL state (search text, category, sort, account), rendered as
 * a query suffix for the drill-down to carry. Without it, opening a contact
 * from a filtered list and backing out lands on the default list instead of
 * the one it was opened from.
 *
 * `contactId` is dropped: the path spells that now, and keeping the legacy
 * param would fight the route on the way back.
 */
export const listQuerySuffix = (searchParams: URLSearchParams): string => {
  const params = new URLSearchParams(searchParams);
  params.delete("contactId");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};
